
// next/image replaced with img;
import { useState } from "react";
import { cn } from "@/lib/utils/cn";
import { getAvatarFallback } from "@/lib/utils/format";

interface AvatarProps {
  src?: string | null;
  alt: string;
  size?: "xs" | "sm" | "md" | "lg" | "xl" | "2xl";
  fallback?: string;
  className?: string;
}

const sizeMap = {
  xs: { container: "w-6 h-6", text: "text-2xs" },
  sm: { container: "w-8 h-8", text: "text-xs" },
  md: { container: "w-10 h-10", text: "text-sm" },
  lg: { container: "w-14 h-14", text: "text-base" },
  xl: { container: "w-20 h-20", text: "text-xl" },
  "2xl": { container: "w-28 h-28", text: "text-3xl" },
};

export function Avatar({
  src,
  alt,
  size = "md",
  fallback,
  className,
  }: AvatarProps) {
  const [error, setError] = useState(false);
  const { container, text } = sizeMap[size];
  const initials = fallback || getAvatarFallback(alt);

  return (
    <span
      className={cn(
        "relative inline-flex flex-shrink-0 rounded-full overflow-hidden",
        "bg-gradient-to-br from-brand/30 to-accent/30",
        container,
        className
      )}
    >
      {src && !error ? (
        <img
          src={src}
          alt={alt}
          className="object-cover"
          onError={() => setError(true)}
          />
      ) : (
        <span
          className={cn(
            "absolute inset-0 flex items-center justify-center",
            "font-bold text-brand select-none",
            text
          )}
        >
          {initials}
        </span>
      )}
    </span>
  );
}

interface AvatarGroupProps {
  avatars: Array<{
    src?: string | null;
    alt: string;
    fallback?: string;
  }>;
  max?: number;
  size?: "xs" | "sm" | "md";
  className?: string;
}

export function AvatarGroup({ avatars, max = 3, size = "sm", className }: AvatarGroupProps) {
  const visible = avatars.slice(0, max);
  const overflow = avatars.length - max;
  const { container } = sizeMap[size];

  return (
    <div className={cn("flex -space-x-2", className)}>
      {visible.map((avatar, index) => (
        <span
          key={index}
          className={cn(
            "relative inline-flex flex-shrink-0 rounded-full overflow-hidden ring-2 ring-background",
            container
          )}
        >
          <Avatar {...avatar} size={size} />
        </span>
      ))}
      {overflow > 0 && (
        <span
          className={cn(
            "relative inline-flex flex-shrink-0 rounded-full overflow-hidden",
            "ring-2 ring-background bg-muted",
            "items-center justify-center",
            container
          )}
        >
          <span className="text-2xs font-medium text-text-muted">+{overflow}</span>
        </span>
      )}
    </div>
  );
}
